import React, { useCallback, useEffect, lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { CssBaseline } from "@mui/material";
import Pathnames from "./model/enum/pathnames";
import TrackPathForAnalytics from "./service/TrackPathForAnalytics";
import ReactGA from "react-ga4";
import Navbar from "./shared/components/navbar/Navbar";
import Homepage from "./components/homepage/Homepage";
import Spinner from "./shared/components/loader/Spinner";
import Cookies from "./shared/components/cookies/Cookies";
import Footer from "./shared/components/footer/Footer";
import "./App.css";

const Request = lazy(() => import("./components/request/Request"));
const Gallery = lazy(() => import("./components/gallery/Gallery"));
const Review = lazy(() => import("./components/review/Review"));
const About = lazy(() => import("./components/about/About"));
const Contact = lazy(() => import("./components/contact/Contact"));
const Errorpage = lazy(() => import("./components/error/Errorpage"));
const TermsAndConditions = lazy(
  () => import("./shared/components/footer/legalTerms/TermsAndConditions"),
);
const PrivacyPolicy = lazy(
  () => import("./shared/components/footer/legalTerms/PrivacyPolicy"),
);
const Appointment = lazy(() => import("./components/appointment/Appointment"));
const Finance = lazy(() => import("./components/finance/Finance"));
const ThanksPage = lazy(
  () => import("./shared/components/greetings/ThanksPage"),
);
const ListServices = lazy(() => import("./components/service/ListServices"));
const Category = lazy(
  () => import("./components/service/list/selected_category/Category"),
);
const Admin = lazy(() => import("./administration/Admin"));
const EstimatePreviewDocument = lazy(
  () => import("./components/estimate/EstimatePreviewDocument"),
);

ReactGA.initialize("AW-16663821488");

const App = () => {
  const { search } = useLocation();
  const location = useLocation();

  let pathname = location.pathname;

  const analytics = useCallback(() => {
    if (!isAdminPage()) {
      TrackPathForAnalytics({
        path: pathname,
        search: search,
        title: pathname.split("/")[1],
      });
    }
  }, [pathname, search]);

  useEffect(() => {
    analytics();
  }, [analytics]);

  useEffect(() => {
    if (!isAdminPage()) ReactGA.send(pathname + search);
  }, [location]);

  const isAdminPage = () => {
    return pathname.includes("admin");
  };

  return (
    <>
      <CssBaseline />
      <div className="App">
        {!isAdminPage() ? (
          <>
            <header>{<Navbar />}</header>
            <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path={Pathnames.HOMEPAGE} element={<Homepage />} />
              <Route path={Pathnames.REQUEST} element={<Request />} />
              <Route path={Pathnames.GALLERY} element={<Gallery />} />
              <Route path={Pathnames.REVIEWS} element={<Review />} />
              <Route path={Pathnames.ABOUT} element={<About />} />
              <Route path={Pathnames.CONTACT} element={<Contact />} />
              <Route path={Pathnames.WILDCARD} element={<Errorpage />} />
              <Route path={Pathnames.SERVICE} element={<ListServices />} />
              <Route
                path={Pathnames.SERVICE + "/*"}
                element={<Category />}
              />
              <Route path={Pathnames.TERMS} element={<TermsAndConditions />} />
              <Route path={Pathnames.PRIVACY} element={<PrivacyPolicy />} />
              <Route path={Pathnames.APPOINTMENT} element={<Appointment />} />
              <Route path={Pathnames.FINANCE} element={<Finance />} />
              <Route
                path={Pathnames.THANK_YOU_APPOINTMENT}
                element={<ThanksPage service={"appointment"} />}
              />
              <Route
                path={Pathnames.THANK_YOU_REQUEST}
                element={<ThanksPage service={"request"} />}
              />
              <Route
                path={Pathnames.THANK_YOU_CONTACT}
                element={<ThanksPage service={"contact"} />}
              />
              <Route
                path={Pathnames.ESTIMATE_PREVIEW}
                element={<EstimatePreviewDocument />}
              />
            </Routes>
            </Suspense>
            <Cookies />
            <Footer />
          </>
        ) : (
          <Suspense fallback={<Spinner />}>
            <Admin />
          </Suspense>
        )}
      </div>
    </>
  );
};

export default App;
